import dayjs from 'dayjs/esm';
import { environment } from '#environment';
import customParseFormat from 'dayjs/esm/plugin/customParseFormat';
import utc from 'dayjs/esm/plugin/utc';
import timezone from 'dayjs/esm/plugin/timezone';
// Should more languages need to be supported, they need to be imported here. By default, only the English locale is imported.
// https://day.js.org/docs/en/i18n/changing-locale
if (environment.featureFlags.isLanguageSwitchEnabled) {
  import('dayjs/esm/locale/fr');
}

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

type InputDate = string | Date | number;

export class DateUtils {
  public static newInTimezone(date: InputDate): dayjs.Dayjs {
    return dayjs.tz(date);
  }

  public static changeTimezone(timeZone: string): void {
    dayjs.tz.setDefault(timeZone);
  }

  public static changeLocale(locale: string): void {
    dayjs.locale(locale);
  }

  public static new(date?: InputDate): dayjs.Dayjs {
    return dayjs(date);
  }

  public static today(): string {
    return dayjs().toDate().toDateString();
  }

  public static todayFormatted(): string {
    return dayjs().format('YYYY-MM-DD');
  }

  public static format(template: string, date?: InputDate): string {
    return dayjs(date).format(template);
  }

  public static newFromCustomFormat(dateString: string, format: string): dayjs.Dayjs {
    return dayjs(dateString, format);
  }

  public static isCurrentDateBetweenDates(startDate: Date, endDate: Date): boolean {
    return dayjs().isAfter(startDate) && dayjs().isBefore(endDate);
  }

  public static isFirstDateBeforeSecondDate(firstDate: string, secondDate: string): boolean {
    return dayjs(firstDate).isBefore(dayjs(secondDate));
  }

  public static subtractMonthsFromDate(date: InputDate, numberOfMonths: number): string {
    return dayjs(date).subtract(numberOfMonths, 'month').format('YYYY-MM-DD');
  }

  public static readonly getStartOfXYearsAgo = (numberOfYears: number) =>
    dayjs().startOf('year').subtract(numberOfYears, 'year').format('YYYY-MM-DD');

  public static addThirtyDaysToDate(date: InputDate, format: string): string {
    return dayjs(date).add(30, 'day').format(format);
  }

  public static getEndOfYearForDate(date?: InputDate): string {
    return dayjs(date).endOf('year').format('YYYY-MM-DD');
  }

  public static getStartOfDayForDate(date?: InputDate): number {
    return DateUtils.new(date).startOf('day').toDate().getTime();
  }
}
